// ═══════════════════════════════════════════════
//   AUTH — boot sequence, session, login/signup/logout, desktop init.
//   Loaded after windows.js, before bootstrap.js.
//
//   Exposes (global):
//     bootNext()          — advance boot screen, then checkSession
//     checkSession()      — restore session from Supabase, route to desktop or login
//     showLogin()         — hide boot, show login UI
//     switchTab(m)        — toggle login/signup form
//     doLogin()           — submit login or signup
//     doLogout()          — sign out + reset shared state + return to login
//     enterDesktop()      — post-login bootstrap: open desktop, render every feature,
//                            start tama decay, clock and countdown timers.
//
// ═══════════════════════════════════════════════

const {S, CONFIG, BG_IMGS, DB, SFX, addPts, toast, scheduleSave, loadProfile, pushSave, openWin, closeWin, drag, front, initResizable} = window;

// ══════════════════════════════════════════
// BOOT SEQUENCE
// ══════════════════════════════════════════
const bootMsgs = ['Initializing La Madriguera OS...', 'Loading konbini assets...', 'Connecting to .bun network...', 'Warming up the gachapon...', 'Welcome!'];
let bi = 0;

function bootNext() {
    bi++;
    document.getElementById('bfill').style.width = (bi / bootMsgs.length * 100) + '%';
    document.getElementById('bmsg').textContent = bootMsgs[bi - 1] || '';
    if (bi < bootMsgs.length) setTimeout(bootNext, 500 + Math.random() * 300);
    else checkMaintenanceThenSession();
}

const MAINT_POLL_MS = 30_000;
let _maintPollTimer = null;
let _maintActive = false;

async function fetchMaintenanceState() {
    if (!DB.available()) return null;
    try {
        const { data } = await DB.fetchServerState();
        return data || null;
    } catch (e) {
        console.warn('Server state check failed:', e);
        return null;
    }
}

async function initBoot() {
    const state = await fetchMaintenanceState();
    if (state && state.maintenance) {
        showMaintenance(state);
    } else {
        document.getElementById('boot').style.display = '';
        bootNext();
    }
    startMaintenancePoll();
}

async function checkMaintenanceThenSession() {
    const state = await fetchMaintenanceState();
    if (state && state.maintenance) {
        showMaintenance(state);
        return;
    }
    checkSession();
}

function startMaintenancePoll() {
    if (_maintPollTimer) clearInterval(_maintPollTimer);
    _maintPollTimer = setInterval(async () => {
        const state = await fetchMaintenanceState();
        const on = !!(state && state.maintenance);
        if (on && !_maintActive) {
            showMaintenance(state);
        } else if (!on && _maintActive) {
            // Server came back while we're on the maintenance screen — full reload
            // is the safest way to re-enter the regular boot/session flow.
            window.location.reload();
        } else if (on && _maintActive && state) {
            // Refresh message/eta in case they were updated.
            applyMaintenanceCopy(state);
        }
    }, MAINT_POLL_MS);
}

function applyMaintenanceCopy(state) {
    if (state.message) {
        const msg = document.getElementById('maint-msg');
        if (msg) msg.textContent = state.message;
    }
    if (state.eta) {
        const eta = document.getElementById('maint-eta');
        if (eta) eta.textContent = state.eta;
    }
}

function showMaintenance(state) {
    _maintActive = true;
    document.getElementById('boot').style.display = 'none';
    document.getElementById('s-login').style.display = 'none';
    document.getElementById('s-desktop').style.display = 'none';
    const taskbar = document.getElementById('taskbar');
    if (taskbar) taskbar.style.display = 'none';
    document.querySelectorAll('.win').forEach(w => w.style.display = 'none');
    const popMiss = document.getElementById('pop-miss');
    if (popMiss) popMiss.style.display = 'none';
    const popWarn = document.getElementById('pop-warn');
    if (popWarn) popWarn.style.display = 'none';
    try {
        const bgm = document.getElementById('bgm');
        if (bgm) { bgm.pause(); }
    } catch (e) {}
    const m = document.getElementById('s-maintenance');
    if (!m) return;
    if (state) applyMaintenanceCopy(state);
    m.style.display = 'flex';
}

async function checkSession() {
    if (!DB.available()) {
        showLogin();
        return;
    }
    let done = false;
    const t = setTimeout(() => {
        done = true;
        showLogin();
    }, 8000);
    try {
        const {data: {session}} = await DB.getSession();
        clearTimeout(t);
        if (done) return; // timeout already fired, don't double-navigate
        if (session) {
            try {
                await loadProfile(session.user.id);
            } catch (e) {
                console.warn('Profile load failed:', e);
            }
            enterDesktop();
        } else {
            showLogin();
        }
    } catch (e) {
        clearTimeout(t);
        if (!done) {
            console.warn('Session check failed:', e);
            showLogin();
        }
    }
}

function showLogin() {
    document.getElementById('boot').style.display = 'none';
    const l = document.getElementById('s-login');
    l.style.display = 'flex';
    l.style.alignItems = 'center';
    l.style.justifyContent = 'center';
    setTimeout(() => _placeRandom('pop-warn'), 1500 + Math.random() * 2000);
}


// ══════════════════════════════════════════
// AUTH — login / signup / logout
// ══════════════════════════════════════════
function switchTab(m) {
    const isSignup = m === 'signup';
    document.getElementById('ltab-login').classList.toggle('on', !isSignup);
    document.getElementById('ltab-signup').classList.toggle('on', isSignup);
    document.getElementById('l-username').style.display = isSignup ? 'block' : 'none';
    document.getElementById('l-hint').textContent = isSignup ? 'pick a cool display name! (^w^)' : 'welcome back!';
    document.getElementById('lerr').textContent = '';
}

async function doLogin() {
    const isSignup = document.getElementById('ltab-signup').classList.contains('on');
    const email = document.getElementById('l-email').value.trim();
    const pass = document.getElementById('l-pass').value;
    const lerr = document.getElementById('lerr');
    const btn = document.getElementById('l-btn');
    if (!email) {
        lerr.textContent = 'Enter your email!';
        return;
    }
    if (!pass || pass.length < 6) {
        lerr.textContent = 'Password needs 6+ chars!';
        return;
    }
    if (!DB.available()) {
        lerr.textContent = 'Server unavailable — please refresh the page.';
        return;
    }
    btn.textContent = '...';
    btn.disabled = true;
    lerr.textContent = '';
    if (isSignup) {
        const username = document.getElementById('l-username').value.trim();
        if (!username) {
            lerr.textContent = 'Pick a display name!';
            btn.textContent = '♡';
            btn.disabled = false;
            return;
        }
        const displayName = username.toUpperCase().slice(0, 12);
        const {data, error} = await DB.signUp({
            email, password: pass,
            options: {data: {username: displayName}}
        });
        if (error) {
            lerr.textContent = error.message;
            btn.textContent = '♡';
            btn.disabled = false;
            return;
        }
        S.profileData.name = displayName;
    } else {
        const {data, error} = await DB.signIn({email, password: pass});
        if (error) {
            lerr.textContent = error.message;
            btn.textContent = '♡';
            btn.disabled = false;
            return;
        }
        await loadProfile(data.user.id);
    }
    btn.textContent = '♡';
    btn.disabled = false;
    enterDesktop();
}

async function doLogout() {
    if (DB.available()) {
        await pushSave();
        await DB.signOut();
    }
    // Reset all persisted state from the registry.
    window.resetPersistedState();
    // Reset non-persisted runtime state (UI selection, transient quiz state).
    S.selTama = null;
    S.selBall = null;
    S.curTamaTab = 'care';
    S.traitAnswers = [];
    S.traitQIdx = 0;
    S.shuffledTraitQs = [];
    if (_decayInterval) {
        clearInterval(_decayInterval);
        _decayInterval = null;
    }
    if (_idleTimer) {
        clearInterval(_idleTimer);
        _idleTimer = null;
    }
    document.getElementById('pop-miss').style.display = 'none';
    if (window.resetQuiz) window.resetQuiz();
    if (window.resetTamaModal) window.resetTamaModal();
    if (window.resetScores) window.resetScores();
    document.getElementById('s-desktop').style.display = 'none';
    document.getElementById('taskbar').style.display = 'none';
    document.querySelectorAll('.win').forEach(w => w.style.display = 'none');
    try {
        const bgm = document.getElementById('bgm');
        bgm.pause();
        S.audPlaying = false;
    } catch (e) {
    }
    document.getElementById('l-email').value = '';
    document.getElementById('l-pass').value = '';
    document.getElementById('l-username').value = '';
    document.getElementById('lerr').textContent = '';
    switchTab('login');
    showLogin();
}

let _decayInterval = null;

// ── Popup placement helper ─────────────────────────────────────────────────
const _POP_PAD  = 12;
const _TASKBAR_H = 52;

function _placeRandom(id, bottomOffset) {
    bottomOffset = bottomOffset || 0;
    const el = document.getElementById(id);
    if (!el) return;
    el.style.visibility = 'hidden';
    el.style.display    = 'block';
    el.style.right      = 'auto';
    const w = el.offsetWidth, h = el.offsetHeight;
    // Body may have CSS zoom; innerWidth/Height are viewport coords, but
    // style.left/top are in layout (un-zoomed) coords. Convert.
    const z = parseFloat(getComputedStyle(document.body).zoom) || 1;
    const vw = window.innerWidth / z;
    const vh = window.innerHeight / z;
    const maxLeft = Math.max(_POP_PAD, vw  - w - _POP_PAD);
    const maxTop  = Math.max(_POP_PAD, vh - bottomOffset - h - _POP_PAD);
    el.style.left = (_POP_PAD + Math.random() * (maxLeft - _POP_PAD)) + 'px';
    el.style.top  = (_POP_PAD + Math.random() * (maxTop  - _POP_PAD)) + 'px';
    el.style.visibility = 'visible';
}

// ── Idle-popup system ──────────────────────────────────────────────────────
// To add a new idle popup: add a _maybeShow* function below and call it from
// _checkIdlePopups(). Each function is responsible for its own "already shown
// today" guard (localStorage key) and its own conditions.

const IDLE_POPUP_DELAY_MS = 3 * 60 * 1000; // show after 3 minutes without activity
let _idleTimer = null;
let _lastActivity = Date.now();
let _idleListenersAttached = false;

function _resetActivity() { _lastActivity = Date.now(); }

function _startIdlePopups() {
    if (_idleTimer) clearInterval(_idleTimer);
    _lastActivity = Date.now();
    if (!_idleListenersAttached) {
        ['mousemove', 'keydown', 'click', 'touchstart'].forEach(ev =>
            document.addEventListener(ev, _resetActivity, {passive: true}));
        _idleListenersAttached = true;
    }
    _idleTimer = setInterval(_checkIdlePopups, 60_000);
}

function _checkIdlePopups() {
    if (Date.now() - _lastActivity < IDLE_POPUP_DELAY_MS) return;
    _maybeShowMissionsPopup();
}

function _maybeShowMissionsPopup() {
    const today = window.todayStr();
    if (localStorage.getItem('pop-miss-shown') === today) return;
    if (!S.missionsData || !S.missionsData.length) return;
    const allDone = S.missionsData.every(m => S.completedMissions.includes(m.id));
    if (allDone) return;
    localStorage.setItem('pop-miss-shown', today);
    _placeRandom('pop-miss', _TASKBAR_H);
}

async function enterDesktop() {
    // Shift login timestamps so the games-window NEW / UPD badges can compare
    // game.addedAt / game.updatedAt against the user's PREVIOUS login. Done
    // before any feature renders so renderGamesList sees the right value.
    S.prevLoginAt = S.lastLoginAt || 0;
    S.lastLoginAt = Date.now();
    scheduleSave();

    try {
        const {data} = await DB.fetchShopItems();
        if (data && data.length) window.SHOP_ITEMS = data;
    } catch(e) {
        console.warn('Failed to load shop items', e);
    }
    // Hide whichever screen is currently showing
    document.getElementById('boot').style.display = 'none';
    document.getElementById('s-login').style.display = 'none';
    document.getElementById('s-desktop').style.display = 'block';
    document.getElementById('taskbar').style.display = 'flex';
    document.getElementById('s-desktop').style.backgroundImage = `url(${BG_IMGS[S.activeBg] || BG_IMGS.hills})`;
    document.getElementById('pop-warn').style.display = 'none';
    const av = document.getElementById('prof-av-img');
    if (av) av.src = `assets/profile/profile_icon${window.profIconIdx || 1}.png`;
    // Tutorial-first onboarding: if the user hasn't seen the welcome tour,
    // open it and chain the trait test on close. Otherwise behave as before
    // (just open the trait test if traits are empty).
    const tutorialOpening = typeof window.maybeShowTutorialOnEnter === 'function'
        ? window.maybeShowTutorialOnEnter()
        : false;
    if (!tutorialOpening && S.profileData.traits.length === 0) {
        setTimeout(() => {
            window.openTraitTest();
            toast('Set up your profile! ✦');
        }, 2500);
    }
    if (typeof window.initTaskbarPostgameToggle === 'function') {
        window.initTaskbarPostgameToggle();
    }
    if (!tutorialOpening && typeof window.maybeShowPostGameOnEnter === 'function') {
        window.maybeShowPostGameOnEnter();
    }
    const bgm = document.getElementById('bgm');
    bgm.volume = S.audVol;
    bgm.play().then(() => {
        S.audPlaying = true;
        document.getElementById('abtn').textContent = '⏸';
        document.getElementById('adisc').classList.add('playing');
        if (window.syncTbAudio) window.syncTbAudio();
    }).catch(() => {
        S.audPlaying = false;
        if (window.syncTbAudio) window.syncTbAudio();
    });
    const tbUser = document.getElementById('tb-user');
    if (tbUser) tbUser.textContent = '@ ' + (S.profileData.name || 'USER');
    startClock();
    initDokomiBeacon();
    if (window.kmRefreshBadges) window.kmRefreshBadges();
    window.buildGacha();
    window.buildStaff();
    window.renderTamaSelect();
    window.renderShop();
    window.renderInvBgs();
    window.buildDailyStars();
    window.updateProfileDisplay();
    window.updateNickList();
    // Sync pts to every DOM element that shows it (loaded value, not just addPts triggers)
    ['prof-pts', 'tb-pts', 'shop-pts'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.textContent = S.pts;
    });
    const _pbar = document.getElementById('prof-bar');
    if (_pbar) _pbar.style.width = Math.min(100, S.pts / 10) + '%';
    // Re-apply mission completion state from loaded data
    window.fetchAndRenderMissions();
    if (S.pts === 0 && S.myAliens.length === 0) {
        addPts(CONFIG.WELCOME_PTS, 'welcome');
        toast(`Welcome! +${CONFIG.WELCOME_PTS} pts for logging in ⭐`);
    }
    // Clear any existing decay interval before starting a new one
    if (_decayInterval) clearInterval(_decayInterval);
    _decayInterval = setInterval(window.tamaDecay, CONFIG.TAMA_DECAY_MS);
    _startIdlePopups();
    setTimeout(initResizable, 400);
}

document.getElementById('l-pass').addEventListener('keydown', e => {
    if (e.key === 'Enter') doLogin();
});


const _TB_DAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

function startClock() {
    function t() {
        const n = new Date();
        const h = n.getHours();
        document.getElementById('ttime').textContent =
            String(h).padStart(2, '0') + ':' + String(n.getMinutes()).padStart(2, '0');
        const dtEl = document.getElementById('tb-datetime');
        if (dtEl) dtEl.textContent =
            _TB_DAYS[n.getDay()] + ' · ' +
            String(n.getDate()).padStart(2, '0') + '/' +
            String(n.getMonth() + 1).padStart(2, '0');
        const stEl = document.getElementById('tb-status');
        if (stEl) {
            const open = h >= CONFIG.KONBINI_OPEN_H && h < CONFIG.KONBINI_CLOSE_H;
            stEl.textContent = open ? '● OPEN' : '○ CLOSED';
            stEl.className = open ? 'tb-open' : 'tb-closed';
        }
    }
    t();
    setInterval(t, 60000);
}

const TB_SEARCH_ITEMS = [
    { label: 'Gacha',         action: () => window.openWin('w-gacha') },
    { label: 'Shop',          action: () => window.openWin('w-shop') },
    { label: 'Games',         action: () => window.openGames() },
    { label: 'Profile',       action: () => window.openWin('w-profile') },
    { label: 'Staff',         action: () => window.openStaff() },
    { label: 'Missions',      action: () => window.openWin('w-missions') },
    { label: 'Scores',        action: () => window.openScores() },
    { label: 'Mail',          action: () => window.openKonmail() },
    { label: 'Files',         action: () => window.openWin('w-files') },
    { label: 'Cameras',       action: () => { window.openWin('w-cam'); window.randomizeCameras(); } },
    { label: 'Logbook',       action: () => window.openLogbook() },
    { label: 'Daily Rewards', action: () => window.openWin('w-daily') },
    { label: 'My Aliens',     action: () => window.openWin('w-tama') },
    { label: 'Bakery',        action: () => window.openBakery() },
    { label: 'About',         action: () => { window.openWin('w-about'); if (window.triggerMission) window.triggerMission('read_bio'); } },
    { label: 'Audio',         action: () => window.openWin('w-audio') },
    { label: 'Tutorial',      action: () => window.openTutorial() },
    { label: 'How to Play',   action: () => window.openTutorial() },
    { label: 'How it Ended',  action: () => window.openPostGame(),
      when: () => typeof window.kmGameDay === 'function' && window.kmGameDay() > 11 },
    { label: 'Closing Time',  action: () => window.openPostGame(),
      when: () => typeof window.kmGameDay === 'function' && window.kmGameDay() > 11 },
];

function tbSearchPick(idx) {
    const item = TB_SEARCH_ITEMS[idx];
    if (!item) return;
    item.action();
    const input = document.getElementById('tb-search');
    const drop = document.getElementById('tb-search-drop');
    if (input) input.value = '';
    if (drop) drop.style.display = 'none';
}

function initSearch() {
    const input = document.getElementById('tb-search');
    const drop = document.getElementById('tb-search-drop');
    if (!input || !drop) return;

    input.addEventListener('input', () => {
        const q = input.value.trim().toLowerCase();
        if (!q) { drop.style.display = 'none'; return; }
        const matches = TB_SEARCH_ITEMS.filter(i =>
            i.label.toLowerCase().includes(q) &&
            (typeof i.when !== 'function' || i.when())
        );
        if (!matches.length) { drop.style.display = 'none'; return; }
        drop.innerHTML = matches.map(m =>
            `<div class="tb-search-item" onmousedown="tbSearchPick(${TB_SEARCH_ITEMS.indexOf(m)})">${m.label}</div>`
        ).join('');
        drop.style.display = 'block';
    });

    input.addEventListener('keydown', e => {
        if (e.key === 'Enter') {
            const q = input.value.trim().toLowerCase();
            const match = TB_SEARCH_ITEMS.find(i =>
                i.label.toLowerCase().includes(q) &&
                (typeof i.when !== 'function' || i.when())
            );
            if (match) { match.action(); input.value = ''; drop.style.display = 'none'; }
        }
        if (e.key === 'Escape') { input.value = ''; drop.style.display = 'none'; }
    });

    input.addEventListener('blur', () => setTimeout(() => { drop.style.display = 'none'; }, 150));
}

document.addEventListener('DOMContentLoaded', initSearch);

const _DOKOMI_START = new Date('2026-05-29T10:00:00');
const _DOKOMI_END   = new Date('2026-05-31T17:00:00');

function _beaconEventStatus() {
    const now = new Date();
    if (now >= _DOKOMI_START && now <= _DOKOMI_END) return 'live';
    if (now > _DOKOMI_END) return 'over';
    return 'soon';
}

function _beaconApplyStatus(st) {
    const statusMap = {
        soon: '★ OPENS SOON',
        live: '● LIVE NOW',
        over: '✦ SEE YOU NEXT TIME',
    };
    const label = statusMap[st] || statusMap.soon;
    for (const id of ['beacon-status', 'beacon-panel-status']) {
        const el = document.getElementById(id);
        if (!el) continue;
        el.className = `beacon-status-pill beacon-status-${st}`;
    }
    for (const id of ['beacon-status-label', 'beacon-panel-status-label']) {
        const el = document.getElementById(id);
        if (el) el.textContent = label;
    }
}

function _beaconUpdateCountdown() {
    const now = new Date();
    const target = now < _DOKOMI_START ? _DOKOMI_START : _DOKOMI_END;
    const diff = target - now;
    const d = diff > 0 ? Math.floor(diff / 86400000) : 0;
    const h = diff > 0 ? Math.floor(diff % 86400000 / 3600000) : 0;
    const m = diff > 0 ? Math.floor(diff % 3600000 / 60000) : 0;
    const dEl = document.getElementById('bcd-d');
    const hEl = document.getElementById('bcd-h');
    const mEl = document.getElementById('bcd-m');
    if (dEl) dEl.textContent = String(d).padStart(2, '0');
    if (hEl) hEl.textContent = String(h).padStart(2, '0');
    if (mEl) mEl.textContent = String(m).padStart(2, '0');
}

function initDokomiBeacon() {
    _beaconUpdateCountdown();
    _beaconApplyStatus(_beaconEventStatus());
    setInterval(() => {
        _beaconUpdateCountdown();
        _beaconApplyStatus(_beaconEventStatus());
    }, 30000);
}

function toggleBeacon() {
    const panel  = document.getElementById('beacon-panel');
    const beacon = document.getElementById('dokomi-beacon');
    const bar    = document.getElementById('beacon-bar');
    const cta    = document.getElementById('beacon-cta');
    if (!panel) return;
    const opening = panel.style.display === 'none' || panel.style.display === '';
    panel.style.display = opening ? 'flex' : 'none';
    if (beacon) {
        if (opening && typeof window.front === 'function') window.front(beacon);
        else beacon.style.zIndex = '';
    }
    if (bar) bar.setAttribute('aria-expanded', String(opening));
    if (cta) cta.textContent = opening ? 'READ MEMO ▴' : 'READ MEMO ▾';
}

function openBeaconCal() {
    const u = new URL('https://calendar.google.com/calendar/render');
    u.searchParams.set('action', 'TEMPLATE');
    u.searchParams.set('text', 'Dokomi 2026 · Visit Clowri · Booth 3X09');
    u.searchParams.set('dates', '20260529T080000Z/20260531T150000Z');
    u.searchParams.set('location', 'Messe Düsseldorf, Stockumer Kirchstraße 61, 40474 Düsseldorf, Germany · Booth 3X09');
    u.searchParams.set('details', 'Come say hi at the Konbini stand! ♥ Booth 3X09. Free sticker for the first 200 visitors. Cosplay welcome!');
    window.open(u.toString(), '_blank', 'noopener');
}

function openBeaconMap() {
    const modal = document.getElementById('beacon-map-modal');
    if (modal) modal.style.display = 'flex';
}

function closeBeaconMap() {
    const modal = document.getElementById('beacon-map-modal');
    if (modal) modal.style.display = 'none';
}

// ── expose to other classic scripts ──
window.bootNext = bootNext;
window.initBoot = initBoot;
window.checkSession = checkSession;
window.checkMaintenanceThenSession = checkMaintenanceThenSession;
window.showMaintenance = showMaintenance;
window.startMaintenancePoll = startMaintenancePoll;
window.showLogin = showLogin;
window.switchTab = switchTab;
window.doLogin = doLogin;
window.doLogout = doLogout;
window.enterDesktop = enterDesktop;
window.startClock = startClock;
window.initDokomiBeacon = initDokomiBeacon;
window.toggleBeacon = toggleBeacon;
window.openBeaconCal = openBeaconCal;
window.openBeaconMap = openBeaconMap;
window.closeBeaconMap = closeBeaconMap;
window.tbSearchPick = tbSearchPick;
